import { create } from 'zustand'

export interface PublishStatus {
  platform: string
  status: 'publishing' | 'success' | 'failed'
  publishId?: string
  error?: string
  /**
   * Machine failure token from the server classifier (e.g. `token_expired`,
   * `image_error`, `app_unaudited`) — present on failure only. Feed to
   * `getPublishFailureCopy` for humanized card copy; never render raw.
   */
  failureReason?: string
  retryable?: boolean
}

const AUTO_CLEAR_MS = 5 * 60 * 1000 // 5 min — clear terminal statuses to prevent unbounded growth

interface PublishStatusStore {
  statuses: Record<string, PublishStatus>
  setPublishing: (creativeId: string, platform: string) => void
  setComplete: (
    creativeId: string,
    data: {
      platform: string
      success: boolean
      publishId?: string
      error?: string
      failureReason?: string
      retryable?: boolean
    }
  ) => void
  clearStatus: (creativeId: string) => void
  getStatus: (creativeId: string) => PublishStatus | undefined
}

export const usePublishStatusStore = create<PublishStatusStore>((set, get) => ({
  statuses: {},
  setPublishing: (creativeId, platform) =>
    set(state => ({
      statuses: {
        ...state.statuses,
        [creativeId]: { platform, status: 'publishing' },
      },
    })),
  setComplete: (creativeId, data) => {
    set(state => ({
      statuses: {
        ...state.statuses,
        [creativeId]: {
          platform: data.platform,
          status: data.success ? 'success' : 'failed',
          publishId: data.publishId,
          error: data.error,
          failureReason: data.failureReason,
          retryable: data.retryable,
        },
      },
    }))
    setTimeout(() => {
      const current = get().statuses[creativeId]
      if (current && current.status !== 'publishing') {
        set(state => {
          const { [creativeId]: _, ...rest } = state.statuses
          return { statuses: rest }
        })
      }
    }, AUTO_CLEAR_MS)
  },
  clearStatus: creativeId =>
    set(state => {
      const { [creativeId]: _, ...rest } = state.statuses
      return { statuses: rest }
    }),
  getStatus: creativeId => get().statuses[creativeId],
}))
